import { SINGLE_POST_MAX_BYTES } from "@ncp/api-client/uploadLimits";
import type { AttachmentUploadResult } from "./types";

/** What a resumable upload needs of the transport (the Surface API client in the app, a fake in tests). */
export interface PartTransport {
  post<T>(path: string, body?: unknown): Promise<T>;
  putBytes<T>(path: string, body: Blob): Promise<T>;
  delete<T>(path: string): Promise<T>;
}

export interface PartProgress { name: string; done: number; total: number }

type Ticket = { uploadId: string; partSize: number; parts: number };

const status = (e: unknown) => (e && typeof e === "object" && "status" in e ? Number((e as { status: unknown }).status) : undefined);
const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Whether a file goes in parts rather than one request (files over 20 MB, up to the 200 MB ceiling). */
export const sendsInParts = (file: { size: number }) => file.size > SINGLE_POST_MAX_BYTES;

/**
 * One chat attachment in 5 MiB parts (3 Nov 2026, when attachments rose to 200 MB): create → raw PUT of each part → complete, which answers
 * as the single upload does. A part is tried three times on a network failure, a 5xx or a 429; a refusal (4xx) stops at once. When the upload
 * fails its staged parts are withdrawn; retrying starts a new one.
 */
export async function uploadInParts(api: PartTransport, base: string, file: File, onProgress?: (p: PartProgress) => void, pause = wait): Promise<AttachmentUploadResult> {
  const ticket = await api.post<Ticket>(base, { fileName: file.name, size: file.size, contentType: file.type || "application/octet-stream" });
  const parts = Math.max(1, ticket.parts);
  try {
    for (let n = 1; n <= parts; n += 1) {
      const from = (n - 1) * ticket.partSize;
      const blob = file.slice(from, Math.min(file.size, from + ticket.partSize));
      for (let attempt = 0; ; attempt += 1) {
        try {
          await api.putBytes(`${base}/${ticket.uploadId}/parts/${n}`, blob);
          break;
        } catch (e) {
          const s = status(e);
          const transient = s === undefined || s >= 500 || s === 429;
          if (!transient || attempt >= 2) throw e;
          await pause(600 * (attempt + 1));
        }
      }
      onProgress?.({ name: file.name, done: n, total: parts });
    }
    return await api.post<AttachmentUploadResult>(`${base}/${ticket.uploadId}/complete`, {});
  } catch (e) {
    void api.delete(`${base}/${ticket.uploadId}`).catch(() => undefined);
    throw e;
  }
}
